import i18n from '@/i18n';
import { imageSpaceAPI } from '@/utils/api';
import { resolveImageUrlToBlob } from '@/utils/imageUrlUtils';
import {
  addDownloadCenterRecord,
  collectKnownImageBytes,
  enqueueDownloadCenterJob,
  estimateClientZipBytes,
  isDownloadTaskCancelled,
  patchDownloadCenterRecord,
  queueBrowserZipDownload,
  registerDownloadTaskAbort,
  shouldUseServerZipDownload,
  submitServerDownloadTask,
  updateDownloadCenterProgress,
} from '@/utils/downloadCenter';
import {
  buildGoodsCompositeSelectionId,
  getDedupedCompositeResultsForGroup,
} from '@/utils/goodsCompositeSelection';

function sanitizeNamePart(name) {
  return String(name || '').replace(/[\\/:*?"<>|]+/g, '_').trim();
}

function getNameWithoutExt(fileName) {
  const name = String(fileName || '').trim();
  if (!name) return '';
  const lastDotIndex = name.lastIndexOf('.');
  return lastDotIndex > 0 ? name.substring(0, lastDotIndex) : name;
}

function normalizeImageRefToId(imageRef) {
  if (imageRef === null || imageRef === undefined) return '';
  if (typeof imageRef === 'string' || typeof imageRef === 'number') return String(imageRef);
  if (typeof imageRef === 'object') return String(imageRef.id || imageRef.imageId || '');
  return '';
}

function makeUniquePath(writtenPaths, zipPath) {
  if (!writtenPaths.has(zipPath)) return zipPath;
  const lastDot = zipPath.lastIndexOf('.');
  const base = lastDot > 0 ? zipPath.slice(0, lastDot) : zipPath;
  const ext = lastDot > 0 ? zipPath.slice(lastDot) : '';
  let c = 1;
  let p = `${base}_${c}${ext}`;
  while (writtenPaths.has(p)) {
    c += 1;
    p = `${base}_${c}${ext}`;
  }
  return p;
}

function extFromUrl(url, fallback) {
  const s = String(url || '');
  const m = s.match(/\.(png|jpe?g|webp|gif)(\?|#|$)/i);
  if (m) return `.${m[1].toLowerCase().replace('jpeg', 'jpg')}`;
  return fallback.startsWith('.') ? fallback : `.${fallback}`;
}

export async function fetchImageBlob(url, signOptions = null) {
  const u = String(url || '').trim();
  if (!u) throw new Error('EMPTY_IMAGE_URL');
  return resolveImageUrlToBlob(u, signOptions || undefined);
}

function triggerBrowserDownload(zipBlob, zipFileName) {
  const url = URL.createObjectURL(zipBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = zipFileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

async function finalizeZipToDownloadCenter({
  zipBlob,
  zipFileName,
  source,
  recordId,
  itemCount,
}) {
  return queueBrowserZipDownload({
    blob: zipBlob,
    fileName: zipFileName,
    source,
    recordId,
    meta: itemCount != null ? { itemCount } : {},
  });
}

function buildFlatZipPathPlan(item, idx, options, defaultExt, writtenPaths) {
  const {
    separator = '',
    separateFolders = false,
    fileNameRules = ['stamp', 'template', 'carousel'],
    folderNameRules = ['stamp', 'template'],
    fixedFileName = '',
    fixedFolderName = '',
  } = options || {};

  const joinParts = (parts) => {
    const cleaned = parts.map((part) => sanitizeNamePart(part)).filter(Boolean);
    return cleaned.length === 0 ? '' : separator ? cleaned.join(separator) : cleaned.join('');
  };

  const stampPart = item.stampStem ?? '';
  const templatePart = item.templateStem ?? '';
  const carouselPart = item.carouselStem ?? `图片${idx + 1}`;
  const ext = item.ext || defaultExt;
  const extNorm = ext.startsWith('.') ? ext : `.${ext}`;

  const folderNameByRule = joinParts(
    folderNameRules.map((key) => {
      if (key === 'fixed') return fixedFolderName;
      if (key === 'stamp') return stampPart;
      if (key === 'template') return templatePart;
      if (key === 'carousel') return carouselPart;
      return '';
    }),
  );
  const folderName = sanitizeNamePart(folderNameByRule) || `组${idx + 1}`;
  const zipPrefix = separateFolders ? `${folderName}/` : '';

  const fileNameStem =
    joinParts(
      fileNameRules.map((key) => {
        if (key === 'fixed') return fixedFileName;
        if (key === 'stamp') return stampPart;
        if (key === 'template') return templatePart;
        if (key === 'carousel') return carouselPart;
        return '';
      }),
    ) ||
    carouselPart ||
    `图片${idx + 1}`;
  const stemSafe = sanitizeNamePart(fileNameStem) || '图片';
  const mainZipPath = makeUniquePath(writtenPaths, `${zipPrefix}${stemSafe}${extNorm}`);
  writtenPaths.add(mainZipPath);

  const planned = [{ url: String(item.url || '').trim(), path: mainZipPath, signOptions: item.signOptions }];

  if (options?.includeOriginal && item.originalUrl && String(item.originalUrl) !== String(item.url)) {
    const origExt = extFromUrl(item.originalUrl, extNorm);
    const origPath = makeUniquePath(writtenPaths, `${zipPrefix}${stemSafe}_原图${origExt}`);
    writtenPaths.add(origPath);
    planned.push({ url: String(item.originalUrl).trim(), path: origPath });
  }

  if (options?.includeBase && item.baseUrl) {
    const baseExt = extFromUrl(item.baseUrl, extNorm);
    const basePath = makeUniquePath(
      writtenPaths,
      `${zipPrefix}${sanitizeNamePart(carouselPart || '底图')}_底图${baseExt}`,
    );
    writtenPaths.add(basePath);
    planned.push({ url: String(item.baseUrl).trim(), path: basePath });
  }

  return planned.filter((row) => row.url);
}

/**
 * 主图创建任务 ZIP（与 FundingTools 列表「确认下载」逻辑一致）
 * @param {object} params
 * @param {object} params.downloadTask
 * @param {object} params.options
 * @param {string} [params.zipFileName]
 */
export async function performGoodsZipDownload({
  downloadTask,
  options,
  zipFileName,
  selectionIds = null,
  source = '主图创建',
}) {
  const goodsForms = downloadTask?.goodsForms || downloadTask?.result?.goodsForms || [];
  if (!downloadTask || goodsForms.length === 0) {
    throw new Error('NO_GOODS_FORMS');
  }

  const safeZipName = zipFileName || `主图创建-${downloadTask.id || 'download'}.zip`;
  const recordId = `dl-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const abortController = new AbortController();
  registerDownloadTaskAbort(recordId, abortController);

  addDownloadCenterRecord({
    id: recordId,
    fileName: safeZipName,
    url: '',
    source,
    status: 'downloading',
    createdAt: new Date().toISOString(),
    completedAt: null,
    downloadedAt: null,
    error: null,
    progress: 0,
  });

  let entries;
  try {
    // 先收集清单并判定闸门，避免服务端路径占用客户端 JSZip 队列槽位
    entries = await collectGoodsZipEntries({
      downloadTask,
      options,
      selectionIds,
    });
  } catch (error) {
    patchDownloadCenterRecord(recordId, {
      status: 'failed',
      error: error?.message || i18n.t('workbench:ipeUtilError.downloadListFailed'),
      progress: 100,
      completedAt: new Date().toISOString(),
    });
    throw error;
  }

  if (entries.length === 0) {
    patchDownloadCenterRecord(recordId, {
      status: 'failed',
      error: i18n.t('workbench:ipeUtilError.downloadNoImages'),
      progress: 100,
      completedAt: new Date().toISOString(),
    });
    throw new Error('NO_SELECTED_ITEMS');
  }

  if (shouldUseServerZipDownload({
    itemCount: entries.length,
    estimatedBytes: estimateClientZipBytes({ itemCount: entries.length }),
  })) {
    try {
      const serverItems = entries.map(({ url, path }) => ({ url, path }));
      const submitted = await submitServerDownloadTask({
        fileName: safeZipName,
        source,
        items: serverItems,
        recordId,
        required: true,
      });
      return {
        queuedId: submitted?.id || null,
        fileName: safeZipName,
        itemCount: entries.length,
        serverSide: true,
      };
    } catch (error) {
      patchDownloadCenterRecord(recordId, {
        status: 'failed',
        error: error?.message || i18n.t('workbench:ipeUtilError.downloadSubmitFailed'),
        progress: 100,
        completedAt: new Date().toISOString(),
      });
      throw error;
    }
  }

  return enqueueDownloadCenterJob(async () => {
    if (isDownloadTaskCancelled(recordId)) {
      return { queuedId: null, fileName: safeZipName, itemCount: 0 };
    }

    const JSZip = (await import('jszip')).default;
    const zip = new JSZip();
    for (let i = 0; i < entries.length; i += 1) {
      if (isDownloadTaskCancelled(recordId)) {
        return { queuedId: null, fileName: safeZipName, itemCount: i };
      }
      const entry = entries[i];
      try {
        const blob = await fetchImageBlob(entry.url);
        zip.file(entry.path, blob);
        updateDownloadCenterProgress(recordId, Math.round(((i + 1) / entries.length) * 80));
      } catch (error) {
        console.warn('[批量下载] 单张图片拉取失败', entry.url, error);
      }
    }

    if (zip.files && Object.keys(zip.files).length === 0) {
      throw new Error('NO_SELECTED_ITEMS');
    }

    updateDownloadCenterProgress(recordId, 85);
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    if (isDownloadTaskCancelled(recordId)) {
      return { queuedId: null, fileName: safeZipName, itemCount: entries.length };
    }

    const queuedId = await finalizeZipToDownloadCenter({
      zipBlob,
      zipFileName: safeZipName,
      source,
      recordId,
      itemCount: entries.length,
    });
    return { queuedId, fileName: safeZipName, itemCount: entries.length };
  });
}

async function collectGoodsZipEntries({ downloadTask, options, selectionIds }) {
  const goodsForms = downloadTask?.goodsForms || downloadTask?.result?.goodsForms || [];
  const {
    separator = '',
    separateFolders = false,
    fileNameRules = ['stamp', 'template', 'carousel'],
    folderNameRules = ['stamp', 'template'],
    fixedFileName = '',
    fixedFolderName = '',
    includeOriginal = false,
    includeBase = false,
  } = options || {};

  const joinParts = (parts) => {
    const cleaned = parts.map((part) => sanitizeNamePart(part)).filter(Boolean);
    return cleaned.length === 0 ? '' : separator ? cleaned.join(separator) : cleaned.join('');
  };

  const { images: imageSpaceImagesData } = await imageSpaceAPI.getAll({ pageSize: 1000 });
  const imageSpaceImages = imageSpaceImagesData || [];
  const imageSpaceById = new Map(imageSpaceImages.map((img) => [String(img.id), img]));

  const selectionSet = selectionIds instanceof Set
    ? selectionIds
    : Array.isArray(selectionIds) && selectionIds.length > 0
      ? new Set(selectionIds.map(String))
      : null;

  const writtenPaths = new Set();
  const entries = [];

  for (let formIndex = 0; formIndex < goodsForms.length; formIndex += 1) {
    const form = goodsForms[formIndex];
    const templateImages = form.templateData?.images || [];
    const groupCount = form.imageGroups?.length || 0;

    for (let groupIndex = 0; groupIndex < groupCount; groupIndex += 1) {
      const groupResults = getDedupedCompositeResultsForGroup(form, groupIndex);
      const imageGroup = form.imageGroups[groupIndex] || {};

      const stampNames = [];
      const stampRefs = [];
      Object.keys(imageGroup).forEach((stampName) => {
        const imageRef = imageGroup[stampName];
        const imageId = normalizeImageRefToId(imageRef);
        if (imageId) {
          const image = imageSpaceById.get(String(imageId));
          if (image && image.name) {
            stampNames.push(getNameWithoutExt(image.name));
          } else {
            stampNames.push(stampName);
          }
          stampRefs.push({ stampName, imageId, imageRef, image });
        }
      });

      const stampPart = joinParts(stampNames);
      const templatePart = getNameWithoutExt(form.templateName || `模板${form.templateId || ''}`);
      const folderNameByRule = joinParts(
        folderNameRules.map((key) => {
          if (key === 'fixed') return fixedFolderName;
          if (key === 'stamp') return stampPart;
          if (key === 'template') return templatePart;
          if (key === 'carousel') return '';
          return '';
        }),
      );
      const folderName = sanitizeNamePart(folderNameByRule) || `组${groupIndex + 1}`;
      const zipPrefix = separateFolders ? `${folderName}/` : '';

      for (let resultIndex = 0; resultIndex < groupResults.length; resultIndex += 1) {
        const result = groupResults[resultIndex];
        if (selectionSet) {
          const selectionId = buildGoodsCompositeSelectionId(formIndex, groupIndex, resultIndex);
          if (!selectionSet.has(selectionId)) continue;
        }
        const templateImage = templateImages.find((img) => img.id === result.imageId);
        const compositeName = getNameWithoutExt(templateImage?.name || '结果');
        const fileNameStem =
          joinParts(
            fileNameRules.map((key) => {
              if (key === 'fixed') return fixedFileName;
              if (key === 'stamp') return stampPart;
              if (key === 'template') return templatePart;
              if (key === 'carousel') return compositeName;
              return '';
            }),
          ) ||
          compositeName ||
          '结果';
        const fileName = `${sanitizeNamePart(fileNameStem) || '结果'}.jpg`;
        const compositeZipPath = makeUniquePath(writtenPaths, `${zipPrefix}${fileName}`);
        writtenPaths.add(compositeZipPath);
        const compositeUrl = String(result.compositeUrl || '').trim();
        if (compositeUrl) {
          entries.push({ url: compositeUrl, path: compositeZipPath });
        }

        if (includeBase && templateImage) {
          const baseUrl = templateImage.url || templateImage.dataUrl || templateImage.src || '';
          if (baseUrl) {
            const baseName = `${sanitizeNamePart(compositeName || '底图')}_底图.jpg`;
            const baseZipPath = makeUniquePath(writtenPaths, `${zipPrefix}${baseName}`);
            writtenPaths.add(baseZipPath);
            entries.push({ url: String(baseUrl).trim(), path: baseZipPath });
          }
        }
      }

      if (includeOriginal) {
        for (const stampRef of stampRefs) {
          let stampUrl =
            stampRef?.imageRef?.url ||
            stampRef?.image?.url ||
            stampRef?.image?.dataUrl ||
            stampRef?.image?.src ||
            '';
          if (!stampUrl && stampRef?.imageId) {
            const serverImg = imageSpaceById.get(String(stampRef.imageId));
            stampUrl = serverImg?.url || serverImg?.dataUrl || '';
          }
          if (!stampUrl) continue;
          const stampFileStem = sanitizeNamePart(
            getNameWithoutExt(stampRef?.image?.name || stampRef.stampName || '原图'),
          );
          const stampZipPath = makeUniquePath(
            writtenPaths,
            `${zipPrefix}${stampFileStem}_原图.jpg`,
          );
          writtenPaths.add(stampZipPath);
          entries.push({ url: String(stampUrl).trim(), path: stampZipPath });
        }
      }
    }
  }

  return entries;
}

/**
 * 扁平图片列表 ZIP（标题提取 / 裁剪 / 放大 / 抠图等详情页）
 * @param {object} params
 * @param {Array<{ url: string, originalUrl?: string, baseUrl?: string, carouselStem?: string, stampStem?: string, templateStem?: string, ext?: string }>} params.items
 */
export async function performFlatImageListZipDownload({
  items,
  options,
  zipFileName,
  defaultExt = '.jpg',
  useDownloadCenter = null,
}) {
  if (!items || items.length === 0) {
    throw new Error('NO_ITEMS');
  }

  const shouldUseDownloadCenter = Boolean(useDownloadCenter) || items.length > 1;
  const safeZipName = zipFileName || 'download.zip';

  if (!shouldUseDownloadCenter) {
    return buildFlatImageListZipDirect({ items, options, zipFileName: safeZipName, defaultExt });
  }

  const recordId = useDownloadCenter?.recordId
    || `dl-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const source = useDownloadCenter?.source || '导出';
  const abortController = new AbortController();
  registerDownloadTaskAbort(recordId, abortController);

  const writtenPaths = new Set();
  const plannedEntries = [];
  for (let idx = 0; idx < items.length; idx += 1) {
    plannedEntries.push(...buildFlatZipPathPlan(items[idx], idx, options, defaultExt, writtenPaths));
  }

  addDownloadCenterRecord({
    id: recordId,
    fileName: safeZipName,
    url: '',
    source,
    status: 'downloading',
    createdAt: new Date().toISOString(),
    completedAt: null,
    downloadedAt: null,
    error: null,
    progress: 0,
    itemCount: useDownloadCenter?.itemCount ?? plannedEntries.length,
  });

  const flatKnownBytes = collectKnownImageBytes(items);
  if (shouldUseServerZipDownload({
    itemCount: plannedEntries.length,
    estimatedBytes: estimateClientZipBytes({
      itemCount: plannedEntries.length,
      knownBytes: flatKnownBytes,
    }),
  })) {
    const submitted = await submitServerDownloadTask({
      fileName: safeZipName,
      source,
      items: plannedEntries.map(({ url, path }) => ({ url, path })),
      recordId,
      required: true,
    });
    return {
      queuedId: submitted?.id || null,
      fileName: safeZipName,
      itemCount: plannedEntries.length,
      serverSide: true,
    };
  }

  return enqueueDownloadCenterJob(async () => {
    const { zipBlob, writtenCount } = await buildFlatImageListZipBlob({
      items,
      options,
      defaultExt,
      recordId,
    });

    if (isDownloadTaskCancelled(recordId)) {
      return { queuedId: null, fileName: safeZipName, itemCount: writtenCount };
    }

    const queuedId = await finalizeZipToDownloadCenter({
      zipBlob,
      zipFileName: safeZipName,
      source,
      recordId,
      itemCount: writtenCount,
    });
    return { queuedId, fileName: safeZipName, itemCount: items.length };
  });
}

async function buildFlatImageListZipBlob({ items, options, defaultExt, recordId }) {
  const JSZip = (await import('jszip')).default;
  const zip = new JSZip();
  const writtenPaths = new Set();
  let writtenCount = 0;

  for (let idx = 0; idx < items.length; idx += 1) {
    if (recordId && isDownloadTaskCancelled(recordId)) {
      break;
    }
    const planned = buildFlatZipPathPlan(items[idx], idx, options, defaultExt, writtenPaths);
    for (const entry of planned) {
      try {
        const blob = await fetchImageBlob(entry.url, entry.signOptions);
        zip.file(entry.path, blob);
        writtenCount += 1;
      } catch (error) {
        console.warn('[批量下载] 单张图片拉取失败', entry.url, error);
      }
    }
    if (recordId) {
      updateDownloadCenterProgress(recordId, Math.round(((idx + 1) / items.length) * 80));
    }
  }

  if (writtenCount === 0) {
    throw new Error('FETCH_ALL_FAILED');
  }

  const zipBlob = await zip.generateAsync({ type: 'blob' });
  return { zipBlob, writtenCount };
}

async function buildFlatImageListZipDirect({ items, options, zipFileName, defaultExt }) {
  const plannedPaths = new Set();
  let plannedCount = 0;
  for (let idx = 0; idx < items.length; idx += 1) {
    plannedCount += buildFlatZipPathPlan(items[idx], idx, options, defaultExt, plannedPaths).length;
  }
  const directKnownBytes = collectKnownImageBytes(items);
  if (shouldUseServerZipDownload({
    itemCount: plannedCount,
    estimatedBytes: estimateClientZipBytes({
      itemCount: plannedCount,
      knownBytes: directKnownBytes,
    }),
  })) {
    throw new Error(i18n.t('workbench:ipeUtilError.downloadTooMany'));
  }
  const { zipBlob } = await buildFlatImageListZipBlob({ items, options, defaultExt, recordId: null });
  triggerBrowserDownload(zipBlob, zipFileName);
  return { queuedId: null, fileName: zipFileName, itemCount: items.length };
}
